import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';

// Mock data
const mockRun = {
  id: 'run_001',
  jobId: 'job_abc123',
  title: 'Le Petit Prince',
  status: 'completed',
  totalEvents: 234,
  duration: '45m 23s',
  startedAt: '2024-01-15T10:30:00Z',
  completedAt: '2024-01-15T11:15:23Z',
};

const summary = {
  pdf_ingestion: { started: '10:30:00', completed: '10:32:00', status: 'completed' },
  image_processing: { started: '10:32:00', completed: '10:47:00', status: 'completed' },
  chapter_detection: { started: '10:47:00', completed: '10:50:00', status: 'completed' },
  synthesize_tts: { started: '10:50:00', completed: '11:10:00', status: 'completed' },
  assemble_audio: { started: '11:10:00', completed: '11:13:00', status: 'completed' },
  generate_credits: { started: '11:13:00', completed: '11:14:00', status: 'completed' },
  qa_finalize: { started: '11:14:00', completed: '11:15:23', status: 'completed' },
};

const events = [
  { timestamp: '10:30:00', event: 'pdf_ingestion_start', metadata: {} },
  { timestamp: '10:32:00', event: 'pdf_ingestion_end', metadata: { pages: 96 } },
  { timestamp: '10:32:01', event: 'image_processor_png_start', metadata: {} },
  { timestamp: '10:47:00', event: 'image_processor_png_end', metadata: { total_pages_processed: 96 } },
  { timestamp: '10:47:01', event: 'chapter_detector_start', metadata: {} },
  { timestamp: '10:50:00', event: 'chapter_detector_end', metadata: { chapters_detected: 27 } },
  { timestamp: '10:50:01', event: 'synthesize_tts_start', metadata: {} },
  { timestamp: '10:50:05', event: 'synthesize_tts_batch_start', metadata: { batch: 1 } },
  { timestamp: '10:50:35', event: 'synthesize_tts_batch_end', metadata: { batch: 1, duration_ms: 3000 } },
  // ... more events
  { timestamp: '11:10:00', event: 'synthesize_tts_end', metadata: { total_batches: 89 } },
  { timestamp: '11:15:23', event: 'qa_finalize_end', metadata: { success: true } },
];

export default function TTSTelemetryRunPage() {
  const { runId } = useParams();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to="/tts/telemetry"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Telemetry
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-slate-800">Run Details</h1>
            <Badge variant="success">Completed</Badge>
          </div>
          <p className="text-slate-500">
            {runId || mockRun.id} • {mockRun.title} • Duration: {mockRun.duration}
          </p>
        </div>
        <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="text-center">
            <p className="text-2xl font-bold text-slate-800">{mockRun.totalEvents}</p>
            <p className="text-sm text-slate-500">Total Events</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-2xl font-bold text-slate-800">{mockRun.duration}</p>
            <p className="text-sm text-slate-500">Duration</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-2xl font-bold text-slate-800">96</p>
            <p className="text-sm text-slate-500">Pages</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-2xl font-bold text-slate-800">27</p>
            <p className="text-sm text-slate-500">Chapters</p>
          </CardBody>
        </Card>
      </div>

      {/* Step Summary */}
      <Card>
        <CardHeader title="Pipeline Steps Summary" />
        <CardBody>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 text-sm font-semibold text-slate-600">Step</th>
                  <th className="text-left py-2 text-sm font-semibold text-slate-600">Started</th>
                  <th className="text-left py-2 text-sm font-semibold text-slate-600">Completed</th>
                  <th className="text-left py-2 text-sm font-semibold text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary).map(([step, data]) => (
                  <tr key={step} className="border-b border-slate-100">
                    <td className="py-3 font-medium text-slate-800 capitalize">
                      {step.replace(/_/g, ' ')}
                    </td>
                    <td className="py-3 text-slate-600 font-mono text-sm">{data.started}</td>
                    <td className="py-3 text-slate-600 font-mono text-sm">{data.completed}</td>
                    <td className="py-3">
                      <Badge variant="success" size="sm">✅ Completed</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* Event Timeline */}
      <Card>
        <CardHeader title="Event Timeline" subtitle="All events in chronological order" />
        <CardBody>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {events.map((event, index) => (
              <div key={index} className="flex items-start gap-4 py-2 border-b border-slate-50">
                <span className="font-mono text-sm text-slate-400 w-20">{event.timestamp}</span>
                <div className="flex-1">
                  <span className="font-medium text-slate-700">{event.event}</span>
                  {Object.keys(event.metadata).length > 0 && (
                    <span className="text-slate-500 text-sm ml-2">
                      {JSON.stringify(event.metadata)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

